<template>
  <div
    class="select-accessible"
    :class="rootClasses"
  >
    <Multiselect
      ref="multiselectRef"
      :classes="multiselectClasses"
      :name="multiselectName"
      :disabled="hasAttributeReadonly || hasAttributeDisabled"
      :mode="multiselectMode"
      :max="configurationMaxOptionsSelected"
      :options="multiselectOptions"
      :placeholder="multiselectPlaceholder"
      :noOptionsText="multiselectPlaceholderNoOptions"
      :noResultsText="multiselectPlaceholderNoResults"
      :openDirection="multiselectOptionsDirectionOpen"
      v-model="multiselectValue"
      valueProp="id"
      label="description"
      trackBy="description"
      autocomplete="nope"
      :searchable="true"
      :canClear="false"
      :canDeselect="false"
      :groups="configurationGroupsEnabled"
      :groupHideEmpty="true"
      :native-support="configurationType === 'multiple'"
      :close-on-select="false"
      @keydown="handleKeydown"
      @click="onMultiselectClick"
      @deselect="onMultiselectDeselect"
      @change="onMultiselectChange"
    />
    <input
      type="hidden"
      :id="props.id"
      :name="inputName"
      class="select-accessible-input"
      :class="inputClasses"
      :required="hasAttributeRequired"
      :data-hash="props.dataHash"
      :data-reference-id="props.dataReferenceId"
      :data-type="configurationType"
      :widget="props.widget"
      v-bind="inputAttributes"
      :value="multiselectValue"
    />
    <template v-if="configurationType === 'multiple' && !(hasAttributeReadonly || hasAttributeDisabled)">
      <span class="select-all-button" type="button" @click="selectAllOptions">
        <i class="eii--fa6-solid--circle-check"></i>
      </span>
      <span class="deselect-all-button" type="button" @click="deselectAllOptions">
        <i class="eii--fa6-solid--circle-xmark"></i>
      </span>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue'

import { ref, onMounted, onUnmounted, nextTick, useAttrs, watch, computed } from 'vue'

import Multiselect from '@vueform/multiselect'

import type {
  PropsUnwrapped,
  SelectElementsDependentEntry,
  SelectValueSingleType,
  SelectValueMultipleType,
  SelectOptionsRetrieveAllEvent,
  SelectOptionsReplaceAllEvent,
  SelectOptionsAddOptionGroupEvent,
  SelectOptionsAddOptionToGroupEvent,
  SelectOptionsAddOptionEvent,
  SelectOptionsRemoveOptionEvent,
  SelectOptionsSelectedChangeEvent,
  SelectOptionsDataSelectionChangeEvent,
  SelectOptionsSelectedAllEvent,
  SelectOptionsSelectedNoneEvent,
  SelectOptionsSelectedCurrent,
  SelectAttributeModifyEvent
} from './types'

const props = defineProps<{
  id: string
  name?: string
  readonly?: string
  required?: string
  disabled?: string
  multiple?: string                     // legacy attribute, not used
  class?: string
  value?: string | number | Array<string | number>
  options?: any[]
  dataHash?: string
  dataDefaultOption?: string            // legacy attribute
  dataReferenceId?: string              // legacy attribute
  dataSelectElementsDependent?: string
  dataPlaceholder?: string
  dataPlaceholderNoOptions?: string
  dataPlaceholderNoResults?: string
  dataOptionsDirectionOpen?: 'top' | 'bottom' | undefined
  widget?: string
  props?: string
}>()

const emit = defineEmits(['hcl-select-accessible-value-changed'])

const hasAttributeName = ref(props.name !== undefined)
const hasAttributeReadonly = ref(props.readonly === 'readonly')
const hasAttributeRequired = ref(props.required === 'required')
const hasAttributeDisabled = ref(props.disabled === 'disabled')

const propsUnwrapped: PropsUnwrapped = props.props ?
  JSON.parse(atob(props.props)) :
  { configuration: {}, data: {} }

const configurationType = propsUnwrapped.configuration?.type ?? 'single'
const configurationGroupsEnabled = ref(propsUnwrapped.configuration?.groups ?? false)
const configurationPlaceholder = propsUnwrapped.configuration?.display?.placeholder ?? '--'
const configurationPlaceholderNoOptions = propsUnwrapped.configuration?.display?.placeholder_no_options ?? '--'
const configurationPlaceholderNoResults = propsUnwrapped.configuration?.display?.placeholder_no_results ?? '--'
const configurationMaxOptionsSelected = propsUnwrapped.configuration?.max_options_selected

const dataOptions = propsUnwrapped.data?.options ?? props.options ?? []
const dataOptionsEsearcherGlobalUndefinedId = '-1'

const dataSelected = propsUnwrapped.data?.selected ?? []

const multiselectRef = ref()
const multiselectName = hasAttributeName.value ? props.name?.replace('[]', '') : undefined
const multiselectMode = configurationType === 'multiple' ? 'tags' : 'single'
const multiselectOptions = ref(dataOptions)

const multiselectPlaceholder = props.dataPlaceholder ? props.dataPlaceholder : configurationPlaceholder
const multiselectPlaceholderNoOptions = props.dataPlaceholderNoOptions ? props.dataPlaceholderNoOptions : configurationPlaceholderNoOptions
const multiselectPlaceholderNoResults = props.dataPlaceholderNoResults ? props.dataPlaceholderNoResults : configurationPlaceholderNoResults
const multiselectOptionsDirectionOpen = props.dataOptionsDirectionOpen ?? 'bottom'

const rootClassesWhitelist = ['error', 'glowing-border-change']
const rootClasses = computed(() => {
  if (props.class) {
    return props.class.split(' ').filter(cls => {
      return rootClassesWhitelist.includes(cls)
    }).join(' ')
  }
  return ''
})

const multiselectClasses = {
  'container': [
    'multiselect', // mandatory, needed for proper rendering of the multiselect
    'multiselect-' + props.id, // legacy, use 'select-accessible-multiselect-*' instead
    'select-accessible-multiselect',
    'select-accessible-multiselect-' + props.id,
    'select-accessible-multiselect-type-' + propsUnwrapped.configuration?.type
  ].join(' ')
}

const inputClassesBlacklist = ['form-control', 'error']
const inputClasses = props.class ? props.class.split(' ').filter(cls => {
  return !inputClassesBlacklist.includes(cls)
}).join(' ') : ''

const inputName = hasAttributeName.value ? props.name?.replace('[]', '_SelectAccessibleInput') : undefined

const attrs = useAttrs()
const inputAttributes = ref({})

const documentLanguage = document.querySelector('body')?.dataset['lang'] ?? 'de'
const documentNoJquery = typeof $ === 'undefined'

const selectElementsDependent: Ref<SelectElementsDependentEntry[]> =
  props.dataSelectElementsDependent ? ref(JSON.parse(props.dataSelectElementsDependent)) : ref([])

const multiselectValue: Ref<SelectValueSingleType | SelectValueMultipleType> = ref()
const multiselectValuePrevious: Ref<SelectValueSingleType | SelectValueMultipleType> = ref()

if (configurationType === 'single') {
  if (props.dataDefaultOption && dataSelected.length === 0) {
    multiselectValue.value = props.dataDefaultOption
  } else if (dataSelected.length > 0) {
    multiselectValue.value = dataSelected[0]
  } else if (Array.isArray(props.value)) {
    multiselectValue.value = props.value[0]
  } else if (props.value !== undefined) {
    multiselectValue.value = props.value
  } else if (multiselectOptions.value.length > 0) {
    if ('label' in multiselectOptions.value[0] && 'options' in multiselectOptions.value[0] && multiselectOptions.value[0].options.length > 0 && 'id' in multiselectOptions.value[0].options[0]) {
      multiselectValue.value = multiselectOptions.value[0].options[0].id
    } else if ('id' in multiselectOptions.value[0]) {
      multiselectValue.value = multiselectOptions.value[0].id
    }
  } else {
    multiselectValue.value = dataOptionsEsearcherGlobalUndefinedId
  }
}

if (configurationType === 'multiple') {
  if (Array.isArray(props.value)) {
    multiselectValue.value = props.value
  } else if (props.value !== undefined) {
    multiselectValue.value = [props.value]
  } else {
    multiselectValue.value = dataSelected
  }
}

multiselectValuePrevious.value = multiselectValue.value

watch(
  () => props.value,
  (n, o) => {
    if (JSON.stringify(n) !== JSON.stringify(o)) {
      if (configurationType === 'single') {
        multiselectValue.value = Array.isArray(n) ? n[0] : n
      }
      if (configurationType === 'multiple') {
        multiselectValue.value = n && !Array.isArray(n) ? [n] : (n ? n : [])
      }
    }
  }, {
  deep: true
})

const selectAllOptions = () => {
  multiselectRef.value.selectAll()
}

const deselectAllOptions = () => {
  multiselectRef.value.clear()
}

const updateConfigurationGroupsEnabled = () => {
  configurationGroupsEnabled.value = multiselectOptions.value.length > 0 && 'label' in multiselectOptions.value[0]
}

const hasOptionWithId = (id: SelectValueSingleType) => {
  let optionExists = false

  if (id === undefined) return optionExists

  const cid = typeof id === 'number' ? id.toString() : id

  if (configurationGroupsEnabled.value) {
    for (const optionsGroup of multiselectOptions.value) {
      for (const option of optionsGroup.options) {
        const coid = typeof option.id === 'number' ? option.id.toString() : option.id
        if (coid === cid) {
          optionExists = true
        }
      }
    }
  } else {
    for (const option of multiselectOptions.value) {
      const coid = typeof option.id === 'number' ? option.id.toString() : option.id
      if (coid === cid) {
        optionExists = true
      }
    }
  }

  return optionExists
}

const filterIdsArrayByOptions = (ids: SelectValueMultipleType) => {
  let idsOptionsExist: number[] = []

  if (ids === undefined) return idsOptionsExist

  const cids = ids.map(element => typeof element === 'number' ? element.toString() : element)

  if (configurationGroupsEnabled.value) {
    for (const optionsGroup of multiselectOptions.value) {
      for (const option of optionsGroup.options) {
        const coid = typeof option.id === 'number' ? option.id.toString() : option.id
        if (cids.includes(coid)) {
          idsOptionsExist.push(option.id)
        }
      }
    }
  } else {
    for (const option of multiselectOptions.value) {
      const coid = typeof option.id === 'number' ? option.id.toString() : option.id
      if (cids.includes(coid)) {
        idsOptionsExist.push(option.id)
      }
    }
  }

  return idsOptionsExist
}

const removeOptionFromOptionsById = (id: string | number | undefined) => {
  if (id === undefined) return

  const cid = typeof id === 'number' ? id.toString() : id

  if (configurationGroupsEnabled.value) {
    for (const group of multiselectOptions.value) {
      const index = group.options.findIndex(option => (typeof option.id === 'number' ? option.id.toString() : option.id) === cid)
      if (index !== -1) {
        group.options.splice(index, 1)
        break
      }
    }
  } else {
    const index = multiselectOptions.value.findIndex(option => (typeof option.id === 'number' ? option.id.toString() : option.id) === cid)
    if (index !== -1) {
      multiselectOptions.value.splice(index, 1)
    }
  }
}

watch(
  () => props.options,
  (n, o) => {
    if (JSON.stringify(n) !== JSON.stringify(o)) {
      multiselectOptions.value = n ?? []

      updateConfigurationGroupsEnabled()

      if (configurationType === 'single') {
        const hasOption = hasOptionWithId(multiselectValue.value as SelectValueSingleType)
        if (!hasOption) {
          multiselectRef.value.clear()
        }
      }

      if (configurationType === 'multiple') {
        const idsOptionsExist = filterIdsArrayByOptions(multiselectValue.value as SelectValueMultipleType)
        multiselectValue.value = idsOptionsExist
      }
    }
  }, {
  deep: true
})

/*
 * Multiselect Click Handling
 *
 * We need to override the handling of the open and close of the options dropdown
 * of the Multiselect Component, because the default behaviour does not match
 * that of a normal browser select.
 */
const multiselectIsOpen = ref(false)

const onMultiselectClick = (event) => {
  // If the multiselect dropdown was closed by clicking somewhere outside of it,
  // we need to update our value since our click handler was not called.
  if (multiselectRef.value.isOpen !== !multiselectIsOpen.value) {
    multiselectIsOpen.value = !multiselectRef.value.isOpen
  }

  // Clicking on the 'search' input itself, opens or closes the dropdown.
  if (event.target.classList.contains('multiselect-search') || event.target.classList.contains('multiselect-tags-search')) {
    if (multiselectIsOpen.value) {
      multiselectRef.value.close()
      multiselectIsOpen.value = false
    } else {
      multiselectRef.value.open()
      multiselectIsOpen.value = true
    }
  }

  // Clicking on an option in the dropdown must close the dropdown.
  if ((event.target.classList.contains('multiselect-option') || event.target.parentElement.classList.contains('multiselect-option'))
    && !event.ctrlKey) {
    multiselectRef.value.close()
    multiselectIsOpen.value = false
  }
}

const onMultiselectDeselect = () => {
  // Only called for configurationType === 'multiple',
  // deselecting, i.e. removing, an option closes the dropdown, update our value.
  multiselectIsOpen.value = false
}

const filterDependentSelectElementsOnData = (
  sourceElementId: string,
  sourceElementName: string | undefined,
  valueNew: SelectValueSingleType | SelectValueMultipleType
) => {
  if (selectElementsDependent.value.length > 0) {
    for (const selectElement of selectElementsDependent.value) {
      const targetElementSelector = "input[name='" + selectElement['name'] + "']"

      // @ts-ignore
      $(targetElementSelector).trigger(
        'select-options-data-selection-change',
        {
          'select-id': sourceElementId,
          'select-name': sourceElementName,
          'data-key': selectElement['data-key'],
          'data-value': valueNew
        } as SelectOptionsDataSelectionChangeEvent
      )
    }
  }
}

// @todo: result seems always empty, is this still needed?
const updateInputAttributes = () => {
  const result = {}

  if ('data' in multiselectRef.value.iv) {
    for (const key in multiselectRef.value.iv.data) {
      result['data-' + key] = multiselectRef.value.iv.data[key]
    }
  }

  for (const key in attrs) {
    result[key] = attrs[key]
  }

  inputAttributes.value = result
}

const onMultiselectChange = (value: any, multiselectInstance: any) => {
  updateInputAttributes()

  if (documentNoJquery) return

  nextTick(() => {
    const sourceElementSelector = "input.select-accessible-input[id='" + props.id + "']"

    // @ts-ignore
    $(sourceElementSelector).trigger('change')

    if (selectElementsDependent.value.length > 0) {
      filterDependentSelectElementsOnData(
        props.id,
        hasAttributeName.value ? props.name : '',
        multiselectValue.value
      )
    }

    // update the value of a dependent input element, replaces "Data Applier"
    if (props.dataReferenceId && multiselectValue.value && multiselectValue.value > dataOptionsEsearcherGlobalUndefinedId) {
      const targetElementId = '#' + props.dataReferenceId
      // @ts-ignore
      const targetElement: HTMLInputElement = document.querySelector(targetElementId)
      // multiselectInstance.iv.description contains the text of the selected value
      targetElement ? targetElement.value = multiselectInstance.iv?.description : null
    }

    if (configurationType === 'single' && value !== undefined) {
      emit('hcl-select-accessible-value-changed', multiselectValue.value)
      multiselectValuePrevious.value = multiselectValue.value
    }

    if (configurationType === 'multiple') {
      // @ts-ignore
      const mvc = multiselectValue.value?.map(element => typeof element === 'number' ? element.toString() : element)
      // @ts-ignore
      const mvpc = multiselectValuePrevious.value?.map(element => typeof element === 'number' ? element.toString() : element)

      if (JSON.stringify(mvc) !== JSON.stringify(mvpc)) {
        emit('hcl-select-accessible-value-changed', multiselectValue.value)
        multiselectValuePrevious.value = multiselectValue.value
      }
    }
  })
}

/*
 * Options Handling
 *
 * Filter Select Option by data key and value, returns the id of the option which should be selected
 *
 * If we find an option with the id idCurrent, we return this id.
 * If we do not find an option with the idCurrent but we have idForced, we return idForced
 * If we do not find an option with the idCurrent, and we have no idForced, we return the id of the option
 * that has the same language as is currently configured for eSearcher and is the default for that language.
 * If none of these apply, give up and return -1.
 */
const filterOptionsByDataKeyAndValue = (
  language: string,
  idForced: string | undefined,
  idCurrent: SelectValueSingleType,
  filterKey: string,
  filterValue: string,
  source: any
) => {
  const optionsFiltered: Ref<any[]> = ref([])

  const idSelected: Ref<string | number | undefined> = ref(undefined)
  const idLanguageDefault: Ref<string | undefined> = ref(undefined)

  if (configurationGroupsEnabled.value) {
    for (const optionsGroup of source) {
      const group = {
        label: optionsGroup.label,
        options: [] as any[]
      }

      for (const option of optionsGroup.options) {
        // always add the default option
        if (option.id === dataOptionsEsearcherGlobalUndefinedId) {
          group.options.push(option)
        }

        // only add other options if filter key and filter value match
        if ('data' in option && filterKey in option.data && option.data[filterKey] === filterValue) {
          group.options.push(option)

          // remember the id of the option which belongs to the language 'language' and is the default
          if ('record-lang' in option.data && 'default' in option.data && option.data['record-lang'] === language && option.data['default'] === '1') {
            idLanguageDefault.value = option.id
          }

          if (idCurrent === option.id) idSelected.value = option.id
        }
      }

      optionsFiltered.value.push(group)
    }
  } else {
    for (const option of source) {
      // always add the default option
      if (option.id === dataOptionsEsearcherGlobalUndefinedId) {
        optionsFiltered.value.push(option)

        if (idCurrent === option.id) idSelected.value = option.id
      }

      // only add other options if filter key and filter value match
      if ('data' in option && filterKey in option.data && option.data[filterKey] === filterValue) {
        optionsFiltered.value.push(option)

        // remember the id of the option which belongs to the language 'language' and is the default
        if ('record-lang' in option.data && 'default' in option.data && option.data['record-lang'] === language && option.data['default'] === '1') {
          idLanguageDefault.value = option.id
        }

        if (idCurrent === option.id) idSelected.value = option.id
      }
    }
  }

  if (!idSelected.value) {
    // we did not find an option with the current id, use fallbacks

    if (idForced) {
      // set selected id to the id that was forced via the component props
      idSelected.value = idForced
    } else if (idLanguageDefault.value) {
      // set selected id to the id that belongs to the language 'language' and is the default
      idSelected.value = idLanguageDefault.value
    } else {
      // give up
      idSelected.value = dataOptionsEsearcherGlobalUndefinedId
    }
  }

  return { optionsFiltered, idSelected }
}

const handleKeydown = (event: Event, instance: Multiselect) => {
  const keyboardEvent = event as KeyboardEvent
  if (keyboardEvent.key === 'Tab' && configurationType === 'single') {
    const multiselectInstance = multiselectRef.value.$refs.multiselect
    const index = getIndexOfPointedOption(multiselectInstance)
    if (index > -1) {
      let allOptions = multiselectOptions.value
      if (configurationGroupsEnabled.value) {
        allOptions = multiselectOptions.value.reduce((acc, group) => {
          return acc.concat(group.options)
        }, [])
      }
      multiselectValue.value = allOptions[index].id
    } 
  }
}

function getIndexOfPointedOption(multiselectInstance: HTMLElement): number {
  const options = multiselectInstance.querySelectorAll(".multiselect-option")
  for (let i = 0; i < options.length; i++) {
    if (options[i].classList.contains("is-pointed")) {
      return i;
    }
  }
  return -1;
}

/*
 * jQuery Listeners for eSearcher jQuery Events
 *
 * Implemented jQuery Events:
 *
 * "input": changes the selected value of the SelectAccessible to the value of the SelectAccessible hidden input element
 *
 * "select-options-retrieve-all": retrieve all options of the SelectAccessible
 *   - payload['result']: contains a copy of all the options
 *
 * "select-options-replace-all": replaces all options of the SelectAccessible with new options
 *   - event['options']: the new options
 *
 * "select-options-add-option-group": add an option group (with optional options) to the SelectAccessible options
 *   - event['group']: object containing the new group (label + options)
 *
 * "select-options-add-option-to-group": add an option to an option group
 *   - event['label']: label of the group to which to add the option to
 *   - event['option']: the new option
 *
 * "select-options-add-option": adds an option to the Select Accessible (Select Accessible without option groups only)
 *   - event['option']: the new option
 *
 * "select-options-remove-option": remove an option of the SelectAccessible via its ID
 *   - event['id']: the id of the option to remove
 *
 * "select-option-selected-change": change the selected value of the SelectAccessible
 *   - event['id']: the id (type = single) or array of ids (type = multiple) of the option(s) which should be selected
 *
 * "select-options-data-selection-change": filters all options of the SelectAccessible using the values of its child 'data' object
 *   - event['data-key']: the key to filter the 'data' object on
 *   - event['data-value']: the value to filter for
 *
 * "select-option-selected-all": select all options (multiple select mode only)
 *
 * "select-option-selected-none": deselect the currently selected option(s) of the SelectAccessible
 *
 * "select-option-selected-current": returns an array of the currently selected option(s)
 *   - payload['result']: array of currently selected options
 *
 * "select-attribute-modify": change the attribute "readonly", "required" or "disabled" of the SA
 *   - event['attribute']: either "readonly", "required" or "disabled"
 *   - event['action']: either "toggle" or "change"
 *   - event['value']: either "true" or "false" (only usable if event['action'] === 'change')
 *
 */
const listenerInput = () => {
  // @ts-ignore
  const val = $(this).val()

  if (configurationType === 'multiple') {
    multiselectValue.value = val === '' ? [] : val
  } else {
    multiselectValue.value = val
  }
}

const listenerSelectOptionsRetrieveAll = (event: any, payload: SelectOptionsRetrieveAllEvent) => {
  payload['result'] = multiselectOptions.value
}

const listenerSelectOptionsReplaceAll = (event: any, payload: SelectOptionsReplaceAllEvent) => {
  if (!payload || !('options' in payload)) return

  multiselectOptions.value = payload.options

  updateConfigurationGroupsEnabled()

  if (configurationType === 'single') {
    const hasOption = hasOptionWithId(multiselectValue.value as SelectValueSingleType)
    if (!hasOption) multiselectRef.value.clear()
  }

  if (configurationType === 'multiple') {
    const idsOptionsExist = filterIdsArrayByOptions(multiselectValue.value as SelectValueMultipleType)
    multiselectValue.value = idsOptionsExist
  }
}

const listenerSelectOptionsAddOptionGroup = (event: any, payload: SelectOptionsAddOptionGroupEvent) => {
  if (!payload || !('group' in payload)) return

  multiselectOptions.value.push(payload.group)

  updateConfigurationGroupsEnabled()
}

const listenerSelectOptionsAddOptionToGroup = (event: any, payload: SelectOptionsAddOptionToGroupEvent) => {
  if (!configurationGroupsEnabled.value && !payload && !('label' in payload) && !('option' in payload)) return

  const group = multiselectOptions.value.filter(option => option.label === payload.label)

  if (group.length === 0) {
    multiselectOptions.value.push({
      label: payload.label,
      options: [
        payload.option
      ]
    })
  } else {
    group[0].options.push(payload.option)
  }
}

const listenerSelectOptionsAddOption = (event: any, payload: SelectOptionsAddOptionEvent) => {
  if (!payload || !('option' in payload)) return

  multiselectOptions.value.push(payload.option)
}

const listenerSelectOptionsRemoveOption = (event: any, payload: SelectOptionsRemoveOptionEvent) => {
  if (!payload || !('id' in payload)) return

  removeOptionFromOptionsById(payload.id)

  if (configurationType === 'single') {
    const idFound = hasOptionWithId(multiselectValue.value as SelectValueSingleType)
    if (!idFound) multiselectRef.value.clear()
  }

  if (configurationType === 'multiple') {
    const idsRemaining = filterIdsArrayByOptions(multiselectValue.value as SelectValueMultipleType)
    multiselectValue.value = idsRemaining
  }
}

const listenerSelectOptionSelectedChange = (event: any, payload: SelectOptionsSelectedChangeEvent) => {
  if (!payload || !('id' in payload)) return

  multiselectValue.value = configurationType === 'multiple' && !Array.isArray(payload.id) ? [payload.id] : payload.id
}

const listenerSelectOptionsDataSelectionChange = (event: any, payload: SelectOptionsDataSelectionChangeEvent) => {
  if (!payload || !('data-key' in payload) || !('data-value' in payload)) return

  if (payload['data-key'] === undefined && payload['data-value'] === undefined) {
    multiselectOptions.value = dataOptions
    return
  }

  const dataKey = payload['data-key']
  const dataValue = typeof payload['data-value'] === 'number' ? payload['data-value'].toString() : payload['data-value'] as string

  const { optionsFiltered, idSelected } = filterOptionsByDataKeyAndValue(
    documentLanguage,
    props.dataDefaultOption ? props.dataDefaultOption : undefined,
    multiselectValue.value as SelectValueSingleType,
    dataKey,
    dataValue,
    dataOptions
  )

  multiselectOptions.value = optionsFiltered.value
  multiselectValue.value = idSelected.value
}

const listenerSelectOptionSelectedAll = (event: any, payload: SelectOptionsSelectedAllEvent) => {
  multiselectRef.value.selectAll()
}

const listenerSelectOptionSelectedNone = (event: any, payload: SelectOptionsSelectedNoneEvent) => {
  multiselectRef.value.clear()
}

const listenerSelectOptionSelectedCurrent = (event: any, payload: SelectOptionsSelectedCurrent) => {
  const result: any[] = []

  if (configurationType === 'multiple') {
    // @ts-ignore
    for (const id of multiselectValue.value) {
      const cid = typeof id === 'number' ? id.toString() : id

      if (configurationGroupsEnabled.value) {
        for (const group of multiselectOptions.value) {
          const index = group.options.findIndex(option => (typeof option.id === 'number' ? option.id.toString() : option.id) == cid)
          if (index !== -1) {
            result.push(group.options[index])
            break
          }
        }
      } else {
        const index = multiselectOptions.value.findIndex(option => (typeof option.id === 'number' ? option.id.toString() : option.id) == cid)
        if (index !== -1) {
          result.push(multiselectOptions.value[index])
        }
      }
    }
  }

  if (configurationType === 'single') {
    const cid = typeof multiselectValue.value === 'number' ? multiselectValue.value.toString() : multiselectValue.value

    if (configurationGroupsEnabled.value) {
      for (const group of multiselectOptions.value) {
        const index = group.options.findIndex(option => (typeof option.id === 'number' ? option.id.toString() : option.id) == cid)
        if (index !== -1) {
          result.push(group.options[index])
          break
        }
      }
    } else {
      const index = multiselectOptions.value.findIndex(option => (typeof option.id === 'number' ? option.id.toString() : option.id) == cid)
      if (index !== -1) {
        result.push(multiselectOptions.value[index])
      }
    }
  }

  payload['result'] = result
}

const listenerSelectAttributeModify = (event: any, payload: SelectAttributeModifyEvent) => {
  if (payload.action === 'change') {
    if (payload.attribute === 'readonly') hasAttributeReadonly.value = payload.value === 'true'
    if (payload.attribute === 'required') hasAttributeRequired.value = payload.value === 'true'
    if (payload.attribute === 'disabled') hasAttributeDisabled.value = payload.value === 'true'
  }

  if (payload.action === 'toggle') {
    if (payload.attribute === 'readonly') hasAttributeReadonly.value = !hasAttributeReadonly.value
    if (payload.attribute === 'required') hasAttributeRequired.value = !hasAttributeRequired.value
    if (payload.attribute === 'disabled') hasAttributeDisabled.value = !hasAttributeDisabled.value
  }
}

/*
 * Setup Handling
 */
onMounted(() => {
  updateInputAttributes()

  if (documentNoJquery) return

  nextTick(() => {
    const sourceElementSelector = "input.select-accessible-input[id='" + props.id + "']"

    // @ts-ignore
    $(sourceElementSelector).on('input', listenerInput)

    // @ts-ignore
    $(sourceElementSelector).on('select-options-retrieve-all', listenerSelectOptionsRetrieveAll)

    // @ts-ignore
    $(sourceElementSelector).on('select-options-replace-all', listenerSelectOptionsReplaceAll)

    // @ts-ignore
    $(sourceElementSelector).on('select-options-add-option-group', listenerSelectOptionsAddOptionGroup)

    // @ts-ignore
    $(sourceElementSelector).on('select-options-add-option-to-group', listenerSelectOptionsAddOptionToGroup)

    // @ts-ignore
    $(sourceElementSelector).on('select-options-add-option', listenerSelectOptionsAddOption)

    // @ts-ignore
    $(sourceElementSelector).on('select-options-remove-option', listenerSelectOptionsRemoveOption)

    // @ts-ignore
    $(sourceElementSelector).on('select-option-selected-change', listenerSelectOptionSelectedChange)

    // @ts-ignore
    $(sourceElementSelector).on('select-options-data-selection-change', listenerSelectOptionsDataSelectionChange)

    // @ts-ignore
    $(sourceElementSelector).on('select-option-selected-all', listenerSelectOptionSelectedAll)

    // @ts-ignore
    $(sourceElementSelector).on('select-option-selected-none', listenerSelectOptionSelectedNone)

    // @ts-ignore
    $(sourceElementSelector).on('select-option-selected-current', listenerSelectOptionSelectedCurrent)

    // @ts-ignore
    $(sourceElementSelector).on('select-attribute-modify', listenerSelectAttributeModify)

    filterDependentSelectElementsOnData(
      props.id,
      hasAttributeName.value ? props.name : '',
      multiselectValue.value
    )

    // @ts-ignore
    $(sourceElementSelector).trigger('select-accessible-initialized')
  })
})

/*
 * Teardown Handling
 */
onUnmounted(() => {
  if (documentNoJquery) return

  const sourceElementSelector = "input.select-accessible-input[id='" + props.id + "']"

  // @ts-ignore
  $(sourceElementSelector).off('input', listenerInput)

  // @ts-ignore
  $(sourceElementSelector).off('select-options-retrieve-all', listenerSelectOptionsRetrieveAll)

  // @ts-ignore
  $(sourceElementSelector).off('select-options-replace-all', listenerSelectOptionsReplaceAll)

  // @ts-ignore
  $(sourceElementSelector).off('select-options-add-option-group', listenerSelectOptionsAddOptionGroup)

  // @ts-ignore
  $(sourceElementSelector).off('select-options-add-option-to-group', listenerSelectOptionsAddOptionToGroup)

  // @ts-ignore
  $(sourceElementSelector).off('select-options-add-option', listenerSelectOptionsAddOption)

  // @ts-ignore
  $(sourceElementSelector).off('select-options-remove-option', listenerSelectOptionsRemoveOption)

  // @ts-ignore
  $(sourceElementSelector).off('select-option-selected-change', listenerSelectOptionSelectedChange)

  // @ts-ignore
  $(sourceElementSelector).off('select-options-data-selection-change', listenerSelectOptionsDataSelectionChange)

  // @ts-ignore
  $(sourceElementSelector).off('select-option-selected-all', listenerSelectOptionSelectedAll)

  // @ts-ignore
  $(sourceElementSelector).off('select-option-selected-none', listenerSelectOptionSelectedNone)

  // @ts-ignore
  $(sourceElementSelector).off('select-option-selected-current', listenerSelectOptionSelectedCurrent)

  // @ts-ignore
  $(sourceElementSelector).off('select-attribute-modify', listenerSelectAttributeModify)

  // @ts-ignore
  $(sourceElementSelector).trigger('select-accessible-deinitialized')
})
</script>

<style src="@vueform/multiselect/themes/default.css">
</style>

<style lang="scss">
.select-accessible {
  width: 100%;
  position: relative;

  .select-all-button,
  .deselect-all-button {
    color: rgb(208, 208, 208);
    background-color: white;
    position: absolute;
    top: calc(50% - 12px);

    i {
      height: 1.2em;
      width: 1.2em;
    }

    &:hover {
      color: rgb(153, 153, 153);
    }
  }

  .select-all-button {
    right: 3.8rem;
  }

  .deselect-all-button {
    right: 2.2rem;
  }

  .multiselect {
    --ms-py: 5px;
    --ms-px: 0.875rem;
    --ms-max-height: 15rem;

    .multiselect-wrapper {

      .multiselect-placeholder,
      .multiselect-single-label {
        white-space: nowrap;
        overflow: hidden;
      }

      .multiselect-placeholder {
        right: 2rem;
      }

      .multiselect-single-label {
        right: 2rem;
        padding-right: var(--ms-px, .875rem);
      }

    }

    &.is-active {
      box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 0.1875rem hsla(var(--theme-color-interaction-h), var(--theme-color-interaction-s), var(--theme-color-interaction-l), 0.15);
      border-color: var(--theme-color-interaction);
    }

    .multiselect-option {
      &.is-selected {
        background-color: var(--theme-color-interaction);
      }
    }

    .multiselect-tags {
      .multiselect-tag {
        background-color: var(--theme-color-interaction);
        white-space: break-spaces;
      }
    }
  }

  .multiselect.select-accessible-multiselect-type-multiple {
    .multiselect-wrapper {
      justify-content: space-between;
    }

    .multiselect-tags {
      max-width: calc(100% - 4rem - var(--ms-px));
    }
  }

  .multiselect.select-accessible-multiselect-type-single {
    .multiselect-tags {
      width: calc(100% - 0.625rem - var(--ms-px));
    }
  }
}

.multiselect-group-label.is-selected,
.multiselect-group-label.is-selected.is-pointed {
  background-color: var(--theme-color-interaction);
}
</style>
